import Table from "@/common/elements/Table";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
} from "@/common/styles";
import { useEffect, useState } from "react";
import Button from "@/common/elements/Button";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  StopOutlined,
} from "@ant-design/icons";
import ClassificationInfoForm from "./ClassificationInfoForm";
import { useDispatch, useSelector } from "react-redux";
import MedicalQuestionnaireModal from "@/components/InmateDetails/MedicalQuestionnaireModal";
import {
  getOptionsFromRefList,
  getRefListNameByID,
  hasCheckSecurity,
  isNonEmptyVariable,
} from "@/util/common_function.js";
import { v4 as uuidV4 } from "uuid";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice.js";
import { evaluateAndApplyQuestionnaireFlags } from "@/util/questionnaireFlagEngine";
import { getQuestionnaireInvolvements } from "@/components/BookingsDetails/JacketDetails/flagUtils";
import { evaluateAndApplyQueueStepEffects } from "@/util/questionnaireEngine";
import { Form, Popconfirm } from "antd";
import dayjs, { isDayjs } from "dayjs";
import API_ENDPOINTS from "@/util/api";
import api from "@/services/axios_custom";
import _ from "lodash";
import {
  Arbitrary_Names,
  Entity_types,
  Security_Types,
} from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const CLASSIFICATION_QUESTIONNAIRES_HEADERS = [
  { key: "classificationEffectiveDate", text: "Effective Date" },
  { key: "classification", text: "Classification" },
  { key: "totalPoints", text: "Total Points" },
  {
    key: "creatorStaff",
    text: "Who filled out the questionnaire?",
  },
  { key: "name", text: "Name" },
];

const ClassificationQuestionnairesList = () => {
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings,
  );

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md).
  const {
    headers: classificationHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig(
    "Bookings:InmateClassificationQuestionnaires",
    CLASSIFICATION_QUESTIONNAIRES_HEADERS,
  );

  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [
    inmateClassificationQuestionnaires,
    setInmateClassificationQuestionnaires,
  ] = useState([]);
  const [singleInmateClassification, setSingleInmateClassification] =
    useState(null);
  const [isModalShown, setIsModalShown] = useState(false);
  const [isEdit, setisEdit] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [questionnaireLoading, setQuestionnaireLoading] = useState(false);
  const [classificationsQuestionnaire, setClassificationsQuestionnaire] =
    useState(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userDetails"));
    if (isNonEmptyVariable(user)) {
      setUserDetails(user);
    }
    getQuestionnaireListFromAPI();
  }, []);

  useEffect(() => {
    if (bookings !== null) {
      let inmateClassificationQuestionnairesCopy =
        bookings?.inmateClassificationQuestionnaires;
      inmateClassificationQuestionnairesCopy =
        inmateClassificationQuestionnairesCopy?.map((i) => {
          return {
            ...i,
            totalPoints: i?.questionnaireInstances?.[0]?.totalPoints,
            classification: getRefListNameByID(
              "Classifications",
              i?.classificationID,
            ),
            creatorStaff: getRefListNameByID("", i?.creatorStaffID, true),
            uuid: uuidV4(),
            name: "Classification",
          };
        });

      if (isNonEmptyVariable(inmateClassificationQuestionnairesCopy)) {
        setInmateClassificationQuestionnaires(
          () => inmateClassificationQuestionnairesCopy,
        );
      } else {
        setInmateClassificationQuestionnaires(() => []);
      }

      if (isNonEmptyVariable(bookings?.arrestReports)) {
        const ar = bookings?.arrestReports?.[0];
        form.setFieldsValue({
          housedForAsOf: isNonEmptyVariable(ar?.housedForAsOf)
            ? dayjs(ar?.housedForAsOf)
            : null,
          needsClassification: ar?.needsClassification,
          nextClassification: isNonEmptyVariable(ar?.nextClassification)
            ? dayjs(ar?.nextClassification)
            : null,
          inmateWorkerEvaluation: ar?.inmateWorkerEvaluation,
        });
      }
    } else {
      setInmateClassificationQuestionnaires(() => []);
      form.setFieldsValue({
        housedForAsOf: null,
        needsClassification: null,
        nextClassification: null,
        inmateWorkerEvaluation: null,
      });
    }
  }, [bookings]);

  const getQuestionnaireListFromAPI = async () => {
    try {
      setQuestionnaireLoading(() => true);
      const url = API_ENDPOINTS.config.questionnaire.questionnairesList;
      const { data } = await api.get(url);
      if (data?.length) {
        let filtered = data?.find((d) => d?.questionnaireID === 5);

        filtered = {
          ...filtered,
          questionGroups: filtered?.questionGroups?.map((qg) => {
            return {
              ...qg,
              questions: qg?.questions?.filter((q) => q?.enabled),
            };
          }),
        };

        setClassificationsQuestionnaire(() => filtered);
      }
    } catch (error) {
      setClassificationsQuestionnaire(() => null);
    } finally {
      setQuestionnaireLoading(() => false);
    }
  };

  const onModalShow = () => {
    setIsModalShown(() => true);
  };

  const onAddQuestionnaireClick = () => {
    setQuestionnaire(classificationsQuestionnaire);
    cancelAction();
    onModalShow();
  };

  const onEditQuestionnaireClick = () => {
    setQuestionnaire(classificationsQuestionnaire);
    setisEdit(true);
    onModalShow();
  };

  const getAnswer = (result) => {
    let resultCopy = _.cloneDeep(result);

    let classificationQuestionnaires = inmateClassificationQuestionnaires;

    if (isNonEmptyVariable(resultCopy)) {
      resultCopy = {
        ...resultCopy,
        questionAnswers: resultCopy?.questionAnswers?.map((q) => {
          if (isDayjs(q.answer)) {
            q.answer = dayjs(q.answer)?.format("MM/DD/YYYY");
          }
          return q;
        }),
      };
    }
    const custodyLevelID = resultCopy?.questionAnswers?.find(
      (q) => q.questionId === 201,
    )?.answer;

    const custodyName = getRefListNameByID(
      "Class_Custody_Level",
      custodyLevelID,
    );

    const classificationList = getOptionsFromRefList(
      "Classifications",
      false,
      false,
      true,
    );

    let classificationID = "";
    if (classificationList?.length && custodyName) {
      classificationList.forEach((c) => {
        if (custodyName.includes(c?.label)) {
          classificationID = c?.value || "";
        }
      });
    }

    // Config-driven flag handling - e.g. "Known gang affiliation?" -> Yes
    // sets the Gang jacket flag, and the Custody Level scale sets/clears the
    // MAXIMUM SECURITY flag as points cross their thresholds. See
    // src/util/questionnaireFlagEngine.js. Stored on this classification's
    // own cdInvolvement (singular - matches the API's nested
    // InmateClassificationQuestionnaires.cdInvolvement shape, see
    // bookings.modal.js), not the booking-root cdInvolvements array, so each
    // classification instance keeps its own flag history. The header flag
    // badges / Flags dialog read both locations back together - see
    // getAllInvolvements in flagUtils.js.
    const { cdInvolvements: cdInvolvement, removedInvIds } =
      setJacketFlagsForQuestionnaire(resultCopy);
    removedInvIds.forEach((invId) =>
      dispatch(setDeleteAPis(API_ENDPOINTS.jacketFlags + invId)),
    );

    const classification = {
      bookingID: bookings?.bookingId,
      creatorStaffID:
        singleInmateClassification?.creatorStaffID ?? userDetails?.staffId,
      inmateClassificationQuestionnaireID:
        singleInmateClassification?.inmateClassificationQuestionnaireID || null,
      classificationID: classificationID || "",
      classificationEffectiveDate: resultCopy?.questionAnswers?.find(
        (q) => q.questionId === 205,
      )?.answer,
      // Stamped once, on first creation, and kept as-is on every later edit -
      // mirrors ASP's Date_Added/Time_Added, which "Related Record Summary"
      // (flagUtils.js) reads back out for the Gang/etc. flag this triggers.
      dateAdded:
        singleInmateClassification?.dateAdded ?? dayjs().format("MM/DD/YYYY"),
      timeAdded:
        singleInmateClassification?.timeAdded ?? dayjs().format("HH:mm"),
      cdInvolvement,
      questionnaireInstances: [resultCopy],
    };

    if (isEdit) {
      // classificationQuestionnaires = classificationQuestionnaires?.map((cq) => {
      //   if (cq?.uuid === singleInmateClassification?.uuid) {
      //     return classification;
      //   }
      //   return cq;
      // });

      classificationQuestionnaires = classificationQuestionnaires?.map((cq) => {
        if (cq?.uuid === singleInmateClassification?.uuid) {
          return {
            ...classification,
            questionnaireInstances: classification?.questionnaireInstances?.map(
              (qi) => {
                return {
                  ...qi,
                  questionnaireInstanceId:
                    singleInmateClassification?.questionnaireInstances?.[0]
                      ?.questionnaireInstanceId,
                  questionAnswers: qi?.questionAnswers?.map((qa) => {
                    const question =
                      singleInmateClassification?.questionnaireInstances?.[0]?.questionAnswers?.find(
                        (q) => q?.questionId === qa?.questionId,
                      );

                    return {
                      ...question,
                      ...qa,
                      answerDate: dayjs()?.format("MM/DD/YYYY"),
                    };
                  }),
                };
              },
            ),
          };
        }
        return cq;
      });
    } else {
      classificationQuestionnaires.push(classification);
    }

    let nextClassification = null;
    if (isNonEmptyVariable(classification?.classificationEffectiveDate)) {
      nextClassification = dayjs(classification?.classificationEffectiveDate)
        .add(3, "month")
        ?.format("MM/DD/YYYY");
    }

    const ar = [
      {
        ...bookings?.arrestReports?.[0],
        housedForAsOf: classification?.classificationEffectiveDate,
        nextClassification: nextClassification,
      },
    ];

    // Config-driven Booking Queue Step handling - e.g. "Classification
    // Complete?" -> Yes/No sets/clears the "03 Classification" step. See
    // applyQueueStepEffectsForQuestionnaire below.
    const { bookingQueueDetails } = applyQueueStepEffectsForQuestionnaire(
      resultCopy,
    );

    dispatch(
      setBookings({
        arrestReports: ar,
        inmateClassificationQuestionnaires: classificationQuestionnaires,
        bookingsQueue: [{ bookingQueueDetails }],
      }),
    );

    cancelAction();
  };

  // Flag handling for this questionnaire - config-driven and generic (see
  // src/util/questionnaireFlagEngine.js for the full rules). Covers both
  // per-question "Set/Clear Jacket Flag" side effects (e.g. Q188 "Known gang
  // affiliation?") and questionnaire/group-level jacketFlagTrigger +
  // scoreThreshold. Idempotent - re-saving the same answers never creates a
  // duplicate flag, and an answer change that no longer triggers a flag
  // retracts it.
  function setJacketFlagsForQuestionnaire(result) {
    // Link the flag back to the classification questionnaire that triggered
    // it so "Related Record Summary" can resolve it later. On a new, unsaved
    // questionnaire the ID isn't assigned yet (relatedObjectID stays null
    // until Save); flagUtils.js's getRelatedRecordSummary falls back to the
    // one still-ID-less draft in inmateClassificationQuestionnaires to
    // resolve it in the meantime.
    const relatedQuestionnaireId =
      singleInmateClassification?.inmateClassificationQuestionnaireID;

    return evaluateAndApplyQuestionnaireFlags({
      questionnaire,
      questionAnswers: result?.questionAnswers,
      questionGroupPoints: result?.questionGroupPoints,
      totalPoints: result?.totalPoints,
      // This classification's own prior flags, not the booking-root
      // cdInvolvements array - see the cdInvolvement assignment in
      // getAnswer above.
      cdInvolvements: getQuestionnaireInvolvements(singleInmateClassification),
      context: {
        invObjId: bookings?.bookingId,
        // The backend stores a saved flag's invObjId as the jacketId, so a
        // re-edit must also accept that when matching its own saved flags.
        altInvObjId: bookings?.jacketId,
        invCreatedBy: userDetails?.staffId,
        invCreatedDate: dayjs().format("MM/DD/YYYY HH:mm:ss"),
        relatedObjectType: "InmateClassificationQuestionnaires",
        relatedObjectID: relatedQuestionnaireId ?? null,
      },
    });
  }

  // Booking Queue Step handling for this questionnaire - config-driven and
  // generic (see src/util/questionnaireEngine.js). Only Set/Clear Queue Step
  // side effects are evaluated at Save, matching legacy's fValidating-only
  // gate for this side-effect type (unlike points/flags, which react live as
  // the form is filled out).
  function applyQueueStepEffectsForQuestionnaire(result) {
    return evaluateAndApplyQueueStepEffects({
      questionnaire,
      questionAnswers: result?.questionAnswers,
      bookingQueueDetails: bookings?.bookingsQueue?.[0]?.bookingQueueDetails,
      context: {
        completionDate: dayjs().format("MM/DD/YYYY"),
        completionTime: dayjs().format("HH:mm"),
        completionStaffID: userDetails?.staffId,
        creatorStaffID: userDetails?.staffId,
      },
    });
  }

  function isValidDateRegex(dateString) {
    const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    return regex.test(dateString);
  }

  const onRowClick = (val) => {
    const newVal = {
      ...val,
      questionnaireInstances: val?.questionnaireInstances?.map((item) => {
        return {
          ...item,
          questionAnswers: item?.questionAnswers?.map((qa) => {
            if (
              isNonEmptyVariable(qa?.answer) &&
              isValidDateRegex(qa?.answer)
            ) {
              return {
                ...qa,
                answer: dayjs(qa?.answer),
              };
            } else {
              return qa;
            }
          }),
        };
      }),
    };

    setSingleInmateClassification(newVal);
  };

  const cancelAction = () => {
    setSingleInmateClassification(null);
    setisEdit(false);
    setIsModalShown(false);
  };

  const deleteClassification = () => {
    const icq = inmateClassificationQuestionnaires?.filter(
      (icq) => icq?.uuid !== singleInmateClassification?.uuid,
    );
    dispatch(
      setBookings({
        inmateClassificationQuestionnaires: icq,
      }),
    );

    if (
      isNonEmptyVariable(
        singleInmateClassification?.inmateClassificationQuestionnaireID,
      )
    ) {
      const url =
        API_ENDPOINTS.inmateClassificationQuestionnaires +
        singleInmateClassification?.inmateClassificationQuestionnaireID;
      dispatch(setDeleteAPis(url));
    }

    cancelAction();
  };

  const updateForm = (val) => {
    const name = val?.[0]?.name?.[0];
    const value = val?.[0]?.value;
    let obj = {};
    if (name === "housedForAsOf" || name === "nextClassification") {
      obj[name] = isNonEmptyVariable(value)
        ? dayjs(value)?.format("MM/DD/YYYY")
        : null;
    } else {
      obj[name] = value;
    }

    dispatch(
      setBookings({
        arrestReports: [
          {
            ...bookings?.arrestReports?.[0],
            ...obj,
          },
        ],
      }),
    );
  };

  return (
    <>
      <Form form={form} onFieldsChange={updateForm}>
        <ClassificationInfoForm bookingsFieldsEnabled={bookingsFieldsEnabled} />
      </Form>
      <StyledSectionHeading>
        <span>Classification Questionnaires</span>
      </StyledSectionHeading>
      <StyledGridRow justify="end">
        <StyledGridCol style={{ paddingBottom: "10px" }}>
          <StyledFlex align="center" gap="10px">
            <Button
              loading={questionnaireLoading}
              onClick={onAddQuestionnaireClick}
              disabled={
                !bookingsFieldsEnabled ||
                isConfiguring ||
                !hasCheckSecurity(
                  Security_Types.functionality,
                  Entity_types.subModule,
                  LABELS_CAPITAL.ADD,
                  MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                  SUB_MODULES_LIST_WITH_ID.Arrest_Report,
                )
              }
              icon={<PlusOutlined />}
            >
              Add
            </Button>
            <Button
              onClick={onEditQuestionnaireClick}
              disabled={
                singleInmateClassification === null ||
                isConfiguring ||
                !hasCheckSecurity(
                  Security_Types.functionality,
                  Entity_types.arbitary,
                  LABELS_CAPITAL.MODIFY,
                  Arbitrary_Names.ModifyInmateClassifications,
                  null,
                  null,
                  null,
                  true,
                )
              }
              icon={<EditOutlined />}
            >
              Details
            </Button>
            {hasCheckSecurity(
              Security_Types.functionality,
              Entity_types.arbitary,
              LABELS_CAPITAL.MODIFY,
              Arbitrary_Names.ModifyInmateClassifications,
            ) && (
              <Popconfirm
                title="Delete the classification"
                description="Are you sure to delete this classification?"
                onConfirm={deleteClassification}
                onCancel={() => {}}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  color="primary"
                  type="primary"
                  icon={<DeleteOutlined />}
                  disabled={
                    singleInmateClassification === null ||
                    !bookingsFieldsEnabled ||
                    isConfiguring ||
                    !hasCheckSecurity(
                      Security_Types.functionality,
                      Entity_types.subModule,
                      LABELS_CAPITAL.DELETE,
                      MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                      SUB_MODULES_LIST_WITH_ID.Arrest_Report,
                    )
                  }
                >
                  Delete
                </Button>
              </Popconfirm>
            )}
            <Button
              onClick={cancelAction}
              disabled={singleInmateClassification === null || isConfiguring}
              icon={<StopOutlined />}
            >
              Cancel
            </Button>

            {/* Config — drag-reorder/resize columns; only while Modify is active */}
            {!isConfiguring ? (
              <Button
                htmlType="button"
                onClick={startConfiguring}
              >
                {configButtonLabel}
              </Button>
            ) : (
              <Popconfirm
                title="Do you want to save the current list view configuration?"
                onConfirm={confirmSave}
                onCancel={discardChanges}
                okText="Yes"
                cancelText="No"
              >
                <Button htmlType="button">
                  {configButtonLabel}
                </Button>
              </Popconfirm>
            )}
          </StyledFlex>
        </StyledGridCol>
      </StyledGridRow>

      {hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.VIEW,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Arrest_Report,
      ) && (
        <Table
          ref={dataTableRef}
          headers={classificationHeaders}
          draggable={isConfiguring}
          data={inmateClassificationQuestionnaires}
          onRowClick={onRowClick}
          onRowDoubleClick={(data) => {
            onRowClick(data);
            onEditQuestionnaireClick();
          }}
          selected={singleInmateClassification === null}
        />
      )}

      {isModalShown && (
        <MedicalQuestionnaireModal
          show={isModalShown}
          onHide={cancelAction}
          questionnaire={questionnaire}
          getAnswer={getAnswer}
          singleQuestionnaire={singleInmateClassification}
          isEdit={isEdit}
        />
      )}
    </>
  );
};
export default ClassificationQuestionnairesList;
